import { FC } from 'react';
import { AllieButton, AllieChip, AllieChipColorProps, AllieText } from '../../../components';
import {
  AllieArrowDownIcon,
  AllieArrowUpIcon,
  AllieCubeOutlineIcon,
  AllieDotIcon,
  AllieTachometerIcon,
  AllieTimeQuarterPastIcon,
} from '../../../styling/icons';
import {
  CardContent,
  HeaderCard,
  InfoOP,
  MobileActionButtonsContainer,
  ProductionOrderStyled,
  StyledDescText,
  StyledMainText,
  VelocityChip,
  VelocityContainer,
  VelocityContent,
} from './ProductionOrder.styles';
import { playCircleOutline } from 'ionicons/icons';
import { ProductionOrderProps } from './ProductionOrder.types';
import { AllieCollapsibleCard } from '../CollapsibleCard';
import { IconData } from './components/IconData';
import { isTablet } from '../../../common/utils';
import { OrderStatus, ToolbarButtons } from './components/ToolbarButtons';

export const ProductionOrder: FC<ProductionOrderProps> = ({
  labels,
  orderId,
  startDate,
  productId,
  expectedSpeedValue,
  velocity,
  velocityVariation,
  velocityUnit,
  status,
  isCurrentShift,
  onClickStartOp,
  onClickCloseOp,
  onClickPauseOp,
  onClickResumeOp,
  onClickSeeAllPOs,
  disabledSeeAllPOs,
  showDrawerButton = true,
  showActionButtons = true,
  isMobile = false,
}) => {
  const mapVelocityVariationChipColor: Record<'up' | 'down', AllieChipColorProps> = {
    up: {
      allieColor: 'success-main',
      backgroundColor: 'success-10',
    },
    down: {
      allieColor: 'error-70',
      backgroundColor: 'error-10',
    },
  };

  const mapVelocityVariationChipBorder: Record<'up' | 'down', string> = {
    up: '1px solid #ABEFC6',
    down: '1px solid var(--allie-color-error-20)',
  };

  const mapProductionStatusChipBorder: Record<string, string> = {
    in_progress: '1.5px solid var(--allie-color-primary-60)',
    done: '1.5px solid var(--allie-color-neutral-60)',
    paused: '1.5px solid var(--allie-color-neutral-60)',
  };
  const mapProductionStatusChipColor: Record<string, string> = {
    in_progress: 'var(--allie-color-neutral-90)',
    done: 'var(--allie-color-neutral-70)',
    paused: 'var(--allie-color-neutral-70)',
  };
  const mapStatusLabel: Record<string, string> = {
    in_progress: labels.status,
    done: labels.statusDone,
    paused: labels.statusPaused,
  };

  return (
    <AllieCollapsibleCard title={labels.cardTitle} isCollapsible={isMobile} initialState={true}>
      <ProductionOrderStyled style={{ padding: isMobile ? '0 12px 12px' : '16px' }}>
        <HeaderCard>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'wrap',
              gap: 10,
              alignItems: 'center',
              width: '100%',
              justifyContent: isMobile ? 'space-between' : 'unset',
            }}
          >
            <StyledMainText color="neutral-90" type="h4" weight={500}>
              {`${labels.productionOrder}${orderId ?? ' -'}`}
            </StyledMainText>
            {status && (
              <AllieChip
                style={{
                  height: 22,
                  cursor: 'default',
                  border: mapProductionStatusChipBorder[status],
                  borderRadius: '50px',
                  padding: '2px 6px',
                  margin: 0,
                  gap: '4px',
                  fontSize: '12px',
                }}
              >
                {status === OrderStatus.InProgress && <AllieDotIcon />}
                <StyledDescText
                  type="body2"
                  style={{
                    color: mapProductionStatusChipColor[status],
                  }}
                  weight={500}
                >
                  {mapStatusLabel[status]}
                </StyledDescText>
              </AllieChip>
            )}
          </div>
          {showActionButtons && !isTablet && (
            <ToolbarButtons
              labels={labels}
              orderId={orderId}
              status={status}
              onClickStartOp={onClickStartOp}
              playCircleOutline={playCircleOutline}
              isCurrentShift={isCurrentShift}
              onClickCloseOp={onClickCloseOp}
              onClickPauseOp={onClickPauseOp}
              onClickResumeOp={onClickResumeOp}
              showDrawerButton={showDrawerButton}
              onClickSeeAllPOs={onClickSeeAllPOs}
              disabledSeeAllPOs={disabledSeeAllPOs}
            />
          )}
        </HeaderCard>
        <CardContent status={status}>
          <InfoOP isMobile={isMobile}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                flexDirection: 'row',
                flexWrap: 'wrap',
              }}
            >
              <IconData
                text={labels.begin}
                icon={<AllieTimeQuarterPastIcon />}
                value={startDate || '-'}
              />
            </div>
            <div
              style={{
                color: 'var(--allie-color-neutral-60)',
                display: 'flex',
                flexDirection: 'row',
                flexWrap: 'wrap',
              }}
            >
              <IconData
                text={labels.product}
                icon={<AllieCubeOutlineIcon />}
                value={productId || '-'}
              />
            </div>
          </InfoOP>

          <VelocityContainer isMobile={isMobile}>
            <VelocityContent>
              <div style={{ display: 'flex', gap: '8px' }}>
                <div style={{ height: 14 }}>
                  <AllieTachometerIcon color="neutral-60" />
                </div>
                <StyledDescText type="body3" color="neutral-60">
                  {labels.velocity}
                </StyledDescText>
              </div>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <StyledMainText color="neutral-90" type="body1" weight={500}>
                  {velocity ? `${velocity} ${velocityUnit}` : '----'}
                </StyledMainText>
                {velocity && velocityVariation && velocityVariation !== 0 && (
                  <VelocityChip
                    color={
                      velocityVariation > 0
                        ? mapVelocityVariationChipColor['up']
                        : mapVelocityVariationChipColor['down']
                    }
                    style={{
                      height: 'auto',
                      border:
                        velocityVariation > 0
                          ? mapVelocityVariationChipBorder['up']
                          : mapVelocityVariationChipBorder['down'],
                    }}
                  >
                    {velocityVariation <= 0 ? (
                      <div style={{ height: 17, marginRight: 4 }}>
                        <AllieArrowDownIcon />
                      </div>
                    ) : (
                      <div style={{ height: 17, marginRight: 4 }}>
                        <AllieArrowUpIcon />
                      </div>
                    )}
                    <AllieText
                      type="body3"
                      color={velocityVariation > 0 ? 'success-70' : 'error-70'}
                      weight={500}
                    >
                      {velocityVariation} %
                    </AllieText>
                  </VelocityChip>
                )}
              </div>
            </VelocityContent>
            {isMobile ? (
              <div
                style={{
                  borderBottom: '1px solid var(--allie-color-neutral-30)',
                  width: '98%',
                  margin: 'var(--allie-spacing-lg) 0',
                }}
              />
            ) : (
              <div style={{ borderLeft: '1px solid var(--allie-color-neutral-30)', height: 54 }} />
            )}
            <VelocityContent>
              <div style={{ display: 'flex', gap: '8px' }}>
                <div style={{ height: 14 }}>
                  <AllieTachometerIcon color="neutral-60" />
                </div>
                <StyledDescText type="body3" color="neutral-60">
                  {labels.expectedSpeed}
                </StyledDescText>
              </div>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <StyledMainText color="neutral-90" type="body1" weight={500}>
                  {expectedSpeedValue ? `${expectedSpeedValue} ${velocityUnit}` : '----'}
                </StyledMainText>
              </div>
            </VelocityContent>
          </VelocityContainer>
        </CardContent>
        {isMobile && (
          <MobileActionButtonsContainer>
            {showDrawerButton && (
              <AllieButton
                allieSize="small"
                style={{ '--border-color': 'var(--allie-color-neutral-40)' }}
                fill="outline"
                full
                allieType="secondary"
                allieColor="neutral"
                corner="rounded-edges-plus"
                onClick={() => !disabledSeeAllPOs && onClickSeeAllPOs()}
                disabled={disabledSeeAllPOs}
              >
                {labels.seeOpButton}
              </AllieButton>
            )}
          </MobileActionButtonsContainer>
        )}
        {showActionButtons && isTablet && (
          <ToolbarButtons
            labels={labels}
            orderId={orderId}
            status={status}
            onClickStartOp={onClickStartOp}
            playCircleOutline={playCircleOutline}
            isCurrentShift={isCurrentShift}
            onClickCloseOp={onClickCloseOp}
            onClickPauseOp={onClickPauseOp}
            onClickResumeOp={onClickResumeOp}
            showDrawerButton={showDrawerButton}
            onClickSeeAllPOs={onClickSeeAllPOs}
            disabledSeeAllPOs={disabledSeeAllPOs}
          />
        )}
      </ProductionOrderStyled>
    </AllieCollapsibleCard>
  );
};
